import slmAxiosClient from './slmAxiosClient';
import {
  CANDIDATE_PROFILE,
  CANDIDATE_PROFILES_SEARCH,
  CUSTOMER_ELIGIBILITY,
  HIRE_CANDIDATE,
  HIRE_CANDIDATE_PHONE,
  HIRE_CANDIDATE_MAIL,
  JOB_POSTINGS,
  REPORT_CONTENT,
  CANDIDATES_METADATA,
  LOCATION_SERVICE,
  ENROLLMENT_SERVICE
} from './URLS';

// Make query from filter config
export function searchParamSerializer(obj) {
  return Object.keys(obj)
    .reduce((acc, key) => {
      if (obj[key] === '' || (obj[key] instanceof Array && !obj[key].length)) {
        return acc;
      }

      const value = obj[key] instanceof Array ? obj[key].join(',') : obj[key];

      return `${acc}&${key}=${encodeURIComponent(value)}`;
    }, '')
    .slice(1);
}

export const getCustomerEligibility = async () => {
  return slmAxiosClient.get(CUSTOMER_ELIGIBILITY);
};

export const searchCandidates = async (filterSettings, page = 1) => {
  const searchQuery = searchParamSerializer(filterSettings);

  const response = await slmAxiosClient.get(`${CANDIDATE_PROFILES_SEARCH}?${searchQuery}&page=${page}`);

  return response.data;
};

export const getCandidateById = async (candidateId) => {
  const response = await slmAxiosClient.get(`${CANDIDATE_PROFILE}/${candidateId}`);

  return response.data;
};

export const markCandidateAsContacted = async (candidateId) => {
  return slmAxiosClient.post(HIRE_CANDIDATE, {
    candidateId
  });
};

export const markCandidateAsPhoned = async (candidateId) => {
  return slmAxiosClient.post(HIRE_CANDIDATE_PHONE, {
    candidateId
  });
};

export const markCandidateAsEmailed = async (candidateId) => {
  return slmAxiosClient.post(HIRE_CANDIDATE_MAIL, {
    candidateId
  });
};

/**
 * Update candidate hire information
 * @param config - updated candidate config
 * @param {number} config.candidateId
 * @param {boolean} config.candidateHired
 */
export const updateHiredStatus = async (candidateId, candidateHired) => {
  return slmAxiosClient.put(HIRE_CANDIDATE, {
    candidateId,
    candidateHired
  });
};

export const fetchRecentlyContactedCandidates = async (page = 1, sort = {}) => {
  const sortQuery = sort.by && sort.order ? `&sortBy=${sort.by}&sortOrder=${sort.order}` : '';
  const response = await slmAxiosClient.get(`${HIRE_CANDIDATE}?page=${page}${sortQuery}`);

  return response.data;
};

export const removeCandidateFromRecentlyContacted = async (candidateId) => {
  return slmAxiosClient.delete(`${HIRE_CANDIDATE}/${candidateId}`);
};

export const reportCandidateContent = async (candidateId, name, flaggedSection) => {
  return slmAxiosClient.post(REPORT_CONTENT, {
    candidateId,
    name,
    flaggedSection
  });
};

export const fetchLocations = async (query, zipRequired) => {
  const response = await slmAxiosClient.get(`${LOCATION_SERVICE}?query=${query}&zipCodeMandatory=${zipRequired}`);
  return response.data;
};

export const fetchCandidatesMetadata = async () => {
  const response = await slmAxiosClient.get(`${CANDIDATES_METADATA}`);
  return response.data;
};

export const fetchJobPostings = async (page = 1, sort = {}) => {
  const sortQuery = sort.by && sort.order ? `&sortBy=${sort.by}&sortOrder=${sort.order}` : '';
  const response = await slmAxiosClient.get(`${JOB_POSTINGS}?page=${page}${sortQuery}`);
  return response.data;
};

export const fetchJobApplicants = async (jobPostingId, page = 1, sort = {}, filter = 'applied') => {
  const sortQuery = sort.by && sort.order ? `&sortBy=${sort.by}&sortOrder=${sort.order}` : '';
  const filterQuery = filter && filter !== 'showAll' ? `&status=${filter}` : '';
  const response = await slmAxiosClient.get(
    `${JOB_POSTINGS}/${jobPostingId}/applicants?page=${page}${sortQuery}${filterQuery}`
  );
  return response.data;
};

export const createJobPosting = async (data) => {
  const response = await slmAxiosClient.post(JOB_POSTINGS, data);
  return response.data;
};

export const updateJobPosting = async (jobPosting) => {
  const response = await slmAxiosClient.put(`${JOB_POSTINGS}/${jobPosting.jobPostingId}`, jobPosting);
  return response.data;
};

export const updateJobPostingStatus = async (jobPostingId, status) => {
  return slmAxiosClient.patch(`${JOB_POSTINGS}/${jobPostingId}`, {
    status
  });
};

export const fetchJobPostingById = async (jobPostingId) => {
  const response = await slmAxiosClient.get(`${JOB_POSTINGS}/${jobPostingId}`);
  return response.data;
};

export const deleteJobPosting = async (jobPostingId) => {
  const response = await slmAxiosClient.delete(`${JOB_POSTINGS}/${jobPostingId}`);
  return response.data;
};

export const acceptJobApplicant = async (jobPostingId, applicantId) => {
  return slmAxiosClient.patch(
    `${JOB_POSTINGS}/${jobPostingId}/applicants/${applicantId}`, { status: 'hired' }
  );
};

export const rejectJobApplicant = async (jobPostingId, applicantId) => {
  return slmAxiosClient.patch(
    `${JOB_POSTINGS}/${jobPostingId}/applicants/${applicantId}`, { status: 'not_hired' }
  );
};

export const fetchRecommendedCandidates = async (jobPostingId, page = 1) => {
  const response = await slmAxiosClient.get(`${JOB_POSTINGS}/${jobPostingId}/recommended-candidates?page=${page}`);
  return response.data;
};

export const inviteCandidateToJobPosting = async (jobPostingId, candidateId) => {
  return slmAxiosClient.post(`${JOB_POSTINGS}/${jobPostingId}/candidates/invite`, {
    candidateId
  });
};

export const inviteCandidateToBulkJobPostings = async (jobPostingIds, candidateId) => {
  return slmAxiosClient.post(`${JOB_POSTINGS}/candidates/bulk-invite`, {
    candidateId,
    jobPostingIds
  });
};

export const requestEnrollmentService = async () => {
  return slmAxiosClient.post(ENROLLMENT_SERVICE);
};

export const fetchActiveJobPostings = async (candidateId, page = 1, sort = {}) => {
  const sortQuery = sort.by && sort.order ? `&sortBy=${sort.by}&sortOrder=${sort.order}` : '';
  const response = await slmAxiosClient.get(`/candidates/${candidateId}/job-postings?page=${page}${sortQuery}`);
  return response.data;
};

export const getPredefinedJobTemplates = (sortBy = 'tradeType', sortOrder = 'asc') => {
  return slmAxiosClient.get(`/job-templates/default?sortBy=${sortBy}&sortOrder=${sortOrder}`);
};

export const getAllJobTemplates = () => {
  return slmAxiosClient.get('/job-templates');
};

export const getJobTemplate = async (tradeType, yearsOfExperience) => {
  // The + sign on the years of experience was causing errors. This is why encoding the URI comp was used
  return slmAxiosClient.get(`/job-templates?tradeType=${encodeURIComponent(tradeType)}&yearsOfExperience=${encodeURIComponent(yearsOfExperience)}`);
};

export const getCompanyProfile = () => {
  return slmAxiosClient.get('/company-profiles');
};

export const createCompanyProfile = (url, description) => {
  return slmAxiosClient.post('/company-profiles', {
    url,
    description
  });
};

export const updateCompanyProfile = (id, url, description) => {
  return slmAxiosClient.put(`/company-profiles/${id}`, {
    url,
    description
  });
};
